import React, { useMemo } from 'react';
import ThemedSelect from '../../../components/ui/ThemedSelect';
import ThemedDateInput from '../../../components/ui/ThemedDateInput';
import { formFieldClass } from '../constants';
import { isSalariedEmployee } from '../utils/validateCreateTaskForm';

const RequiredMark = () => (
    <span className="text-rose-600 dark:text-rose-400" aria-hidden>
        {' '}
        *
    </span>
);

const OptionalMark = () => (
    <span className="ml-1 text-[10px] font-normal text-zinc-400 dark:text-zinc-500">
        (optional)
    </span>
);

const FieldError = ({ message }) =>
    message ? (
        <p
            className="mt-1 flex items-start gap-1 text-[11px] text-rose-600 dark:text-rose-400"
            role="alert"
        >
            <i className="fa-solid fa-circle-exclamation mt-0.5 text-[9px]" />
            <span>{message}</span>
        </p>
    ) : null;

const labelClass =
    'flex items-center gap-1 text-xs font-semibold text-zinc-800 dark:text-zinc-200';

const fieldMt = 'mt-1';

const fieldBorder = (hasError) =>
    hasError
        ? 'border-rose-400/70 focus:border-rose-500 focus:ring-rose-500/30 dark:border-rose-500/50'
        : '';

const SectionLabel = ({ children }) => (
    <p className="text-[10px] font-bold uppercase tracking-wider text-teal-800/90 dark:text-teal-400/90">
        {children}
    </p>
);

const CreateTaskPage = ({
    taskForm,
    onChange,
    onSubmit,
    onCancel,
    taskDraftPreview,
    tokenSymbol,
    isCompanyOwner,
    submitting,
    backendToken,
    employees,
    isEmployeeActive,
    shortenWalletAddress,
    errors = {},
    submitError = '',
    contractConfigured = true,
    hasEnoughBalance = true,
    activeEmployeeCount = 0,
    payrollAssignable = true,
}) => {
    const selectedEmployee = useMemo(
        () =>
            employees.find(
                (employee) => String(employee.id) === String(taskForm.employeeId),
            ) || null,
        [employees, taskForm.employeeId],
    );
    const salariedTask = isSalariedEmployee(selectedEmployee);

    const employeeOptions = [
        { value: '', label: 'Select employee', disabled: true },
        ...employees
            .filter((employee) => isEmployeeActive(employee))
            .map((employee) => ({
                value: employee.id,
                label: `${employee.name} — ${shortenWalletAddress(employee.wallet)}`,
            })),
    ];

    const submitLabel = submitting
        ? 'Creating…'
        : salariedTask
          ? 'Assign task (payroll)'
          : `Create & fund (${tokenSymbol})`;

    const alertClass =
        'rounded-lg border px-3 py-2 text-xs leading-snug';

    return (
        <div className="w-full space-y-4">
            <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="flex min-w-0 items-center gap-3">
                    <button
                        type="button"
                        onClick={onCancel}
                        className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border border-zinc-200 text-zinc-500 transition-colors hover:border-zinc-300 hover:bg-zinc-50 hover:text-zinc-800 dark:border-zinc-700 dark:hover:bg-zinc-800 dark:hover:text-zinc-200"
                        aria-label="Back to dashboard"
                    >
                        <i className="fa-solid fa-arrow-left text-xs" />
                    </button>
                    <div className="min-w-0">
                        <h2 className="text-lg font-bold text-zinc-900 dark:text-white">
                            Create task
                        </h2>
                        <p className="text-xs text-zinc-500 dark:text-zinc-400">
                            {salariedTask
                                ? 'Salaried employees use payroll vault — no per-task USDC lock'
                                : (
                                    <>
                                        Required <span className="text-rose-600">*</span> fields ·
                                        approves exactly the task amount in your wallet
                                    </>
                                )}
                        </p>
                    </div>
                </div>
            </div>

            {!isCompanyOwner ? (
                <div
                    className={`${alertClass} border-amber-200 bg-amber-50 text-amber-950 dark:border-amber-800/40 dark:bg-amber-950/30 dark:text-amber-100`}
                >
                    Only the company owner wallet can create and fund tasks.
                </div>
            ) : null}

            {!contractConfigured ? (
                <div
                    className={`${alertClass} border-amber-300 bg-amber-50 text-amber-950 dark:border-amber-700/40 dark:bg-amber-950/30`}
                >
                    Set <code className="text-[10px]">REACT_APP_TASK_ESCROW_ADDRESS</code>{' '}
                    before creating tasks.
                </div>
            ) : null}

            {activeEmployeeCount === 0 ? (
                <div
                    className={`${alertClass} border-amber-200 bg-amber-50 text-amber-950 dark:border-amber-800/40 dark:bg-amber-950/30`}
                >
                    Add an active employee first (Organization → Employees).
                </div>
            ) : null}

            <div className="w-full rounded-none border-y border-zinc-200 bg-white shadow-sm sm:rounded-xl sm:border dark:border-zinc-800 dark:bg-zinc-950/50">
                <form onSubmit={onSubmit} className="p-4 pt-3" noValidate>
                    {submitError ? (
                        <div
                            className="mb-3 rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-900 dark:border-rose-800/50 dark:bg-rose-950/40 dark:text-rose-100"
                            role="alert"
                        >
                            {submitError}
                        </div>
                    ) : null}

                    <div className="space-y-3">
                        <SectionLabel>
                            Task details
                            <RequiredMark />
                        </SectionLabel>
                        <div className="grid gap-3 sm:grid-cols-2">
                            <div>
                                <label htmlFor="task-title" className={labelClass}>
                                    Title
                                    <RequiredMark />
                                </label>
                                <input
                                    id="task-title"
                                    type="text"
                                    name="title"
                                    value={taskForm.title}
                                    onChange={onChange}
                                    placeholder="Landing page redesign"
                                    className={`${formFieldClass} ${fieldMt} ${fieldBorder(errors.title)}`}
                                    disabled={!isCompanyOwner || submitting}
                                    aria-invalid={Boolean(errors.title)}
                                />
                                <FieldError message={errors.title} />
                            </div>
                            <div>
                                <label className={labelClass}>
                                    Employee
                                    <RequiredMark />
                                </label>
                                <div className={fieldMt}>
                                    <ThemedSelect
                                        name="employeeId"
                                        value={taskForm.employeeId}
                                        onChange={onChange}
                                        disabled={
                                            !isCompanyOwner ||
                                            submitting ||
                                            activeEmployeeCount === 0
                                        }
                                        placeholder="Select employee"
                                        options={employeeOptions}
                                    />
                                </div>
                                <FieldError message={errors.employeeId} />
                            </div>
                        </div>
                        <div>
                            <label htmlFor="task-summary" className={labelClass}>
                                Summary
                                <RequiredMark />
                            </label>
                            <textarea
                                id="task-summary"
                                rows={2}
                                name="summary"
                                value={taskForm.summary}
                                onChange={onChange}
                                placeholder="Deliverables, scope, and expectations."
                                className={`${formFieldClass} ${fieldMt} min-h-[56px] resize-y ${fieldBorder(errors.summary)}`}
                                disabled={!isCompanyOwner || submitting}
                                aria-invalid={Boolean(errors.summary)}
                            />
                            <FieldError message={errors.summary} />
                        </div>
                    </div>

                    <div className="mt-4 space-y-3 border-t border-zinc-200 pt-4 dark:border-zinc-800">
                        <SectionLabel>
                            {salariedTask ? 'Assignment' : 'Payment'}
                            {!salariedTask ? <RequiredMark /> : null}
                        </SectionLabel>
                        {salariedTask ? (
                            <div className="rounded-lg border border-indigo-200 bg-indigo-50/60 px-3 py-2.5 text-xs text-indigo-950 dark:border-indigo-900/40 dark:bg-indigo-950/30 dark:text-indigo-100">
                                {payrollAssignable ? (
                                    <>
                                        This task is covered by the employee&apos;s monthly payroll
                                        vault. No per-task token approval is needed.
                                    </>
                                ) : (
                                    <>
                                        Fund this employee&apos;s monthly payroll on-chain before
                                        assigning tasks (Employees → Fund payroll).
                                    </>
                                )}
                            </div>
                        ) : null}
                        <div className="grid gap-3 sm:grid-cols-2">
                            {!salariedTask ? (
                            <div>
                                <label htmlFor="task-amount" className={labelClass}>
                                    Amount ({tokenSymbol})
                                    <RequiredMark />
                                </label>
                                <input
                                    id="task-amount"
                                    type="number"
                                    min="0"
                                    step="any"
                                    name="amount"
                                    value={taskForm.amount}
                                    onChange={onChange}
                                    placeholder="100"
                                    className={`${formFieldClass} ${fieldMt} tabular-nums ${fieldBorder(errors.amount)}`}
                                    disabled={!isCompanyOwner || submitting}
                                    aria-invalid={Boolean(errors.amount)}
                                />
                                <FieldError message={errors.amount} />
                                {taskForm.amount && !hasEnoughBalance ? (
                                    <p className="mt-1 text-[10px] text-amber-700 dark:text-amber-300">
                                        Balance may be insufficient.
                                    </p>
                                ) : null}
                            </div>
                            ) : null}
                            <div className={salariedTask ? 'sm:col-span-2' : ''}>
                                <label htmlFor="task-due" className={labelClass}>
                                    Due date
                                    <OptionalMark />
                                </label>
                                <ThemedDateInput
                                    id="task-due"
                                    name="dueDate"
                                    value={taskForm.dueDate}
                                    onChange={onChange}
                                    placeholder="Select date"
                                    disabled={!isCompanyOwner || submitting}
                                    hasError={Boolean(errors.dueDate)}
                                    className={fieldMt}
                                />
                                <FieldError message={errors.dueDate} />
                            </div>
                        </div>
                    </div>

                    <div className="mt-4 space-y-3 border-t border-zinc-200 pt-4 dark:border-zinc-800">
                        <SectionLabel>
                            Optional details
                            <OptionalMark />
                        </SectionLabel>
                        <div className="grid gap-3 lg:grid-cols-3">
                            <div className="space-y-3 lg:col-span-2">
                                <div className="grid gap-3 sm:grid-cols-2">
                                    <div>
                                        <label
                                            htmlFor="task-category"
                                            className={labelClass}
                                        >
                                            Category
                                        </label>
                                        <input
                                            id="task-category"
                                            type="text"
                                            name="category"
                                            value={taskForm.category}
                                            onChange={onChange}
                                            placeholder="Frontend"
                                            className={`${formFieldClass} ${fieldMt}`}
                                            disabled={!isCompanyOwner || submitting}
                                        />
                                    </div>
                                    <div>
                                        <label
                                            htmlFor="task-skills"
                                            className={labelClass}
                                        >
                                            Skills
                                        </label>
                                        <input
                                            id="task-skills"
                                            type="text"
                                            name="skills"
                                            value={taskForm.skills}
                                            onChange={onChange}
                                            placeholder="React, UX"
                                            className={`${formFieldClass} ${fieldMt}`}
                                            disabled={!isCompanyOwner || submitting}
                                        />
                                    </div>
                                </div>
                                <div>
                                    <label
                                        htmlFor="task-criteria"
                                        className={labelClass}
                                    >
                                        Acceptance criteria
                                    </label>
                                    <textarea
                                        id="task-criteria"
                                        rows={2}
                                        name="acceptanceCriteria"
                                        value={taskForm.acceptanceCriteria}
                                        onChange={onChange}
                                        placeholder="One per line"
                                        className={`${formFieldClass} ${fieldMt} min-h-[52px] resize-y`}
                                        disabled={!isCompanyOwner || submitting}
                                    />
                                </div>
                            </div>
                            <div className="rounded-lg border border-teal-200/70 bg-teal-50/40 p-2.5 text-[11px] leading-relaxed text-zinc-700 dark:border-teal-900/40 dark:bg-teal-950/20 dark:text-zinc-300 lg:mt-5">
                                <p className="mb-1.5 font-semibold uppercase tracking-wide text-teal-800 dark:text-teal-400">
                                    Preview
                                </p>
                                <p className="truncate">
                                    <span className="text-zinc-500">Cat:</span>{' '}
                                    {taskDraftPreview.category || '—'}
                                </p>
                                <p className="mt-1 line-clamp-2">
                                    <span className="text-zinc-500">Sum:</span>{' '}
                                    {taskDraftPreview.summary || '—'}
                                </p>
                                <p className="mt-1 truncate">
                                    <span className="text-zinc-500">Skills:</span>{' '}
                                    {taskDraftPreview.skills?.length
                                        ? taskDraftPreview.skills.join(', ')
                                        : '—'}
                                </p>
                            </div>
                        </div>
                    </div>

                    <div className="mt-4 flex flex-wrap items-center justify-end gap-2 border-t border-zinc-200 pt-4 dark:border-zinc-800">
                        <button
                            type="button"
                            onClick={onCancel}
                            disabled={submitting}
                            className="rounded-lg border border-zinc-300 bg-zinc-100 px-4 py-2 text-xs font-semibold text-zinc-800 transition-colors hover:bg-zinc-200 disabled:opacity-50 dark:border-zinc-600 dark:bg-zinc-800/70 dark:text-zinc-200"
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            disabled={
                                !isCompanyOwner ||
                                submitting ||
                                !backendToken ||
                                !contractConfigured ||
                                activeEmployeeCount === 0 ||
                                (salariedTask && !payrollAssignable)
                            }
                            className="rounded-lg bg-teal-600 px-5 py-2 text-xs font-semibold text-white shadow-sm transition-colors hover:bg-teal-700 disabled:cursor-not-allowed disabled:opacity-50 dark:hover:bg-teal-500"
                        >
                            {submitLabel}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default CreateTaskPage;
